/**
 * B702 — memberships (B655 a+b+h): the §11 doors over plans, member records
 * and the tenant's membership switches. No screen rides this item; the MCP
 * membrane serves every op here automatically.
 *
 * Who may call (B655 ruling): plan + record WRITES are the back office
 * (STAFF:ADMIN/MANAGER, `entitlement:write`); the counter (STAFF:SELLER —
 * the POS seat) sells, renews and re-issues cards (`order:write`); a
 * CUSTOMER (a B632 session) reads its OWN memberships under the §11 owner
 * rule; the switches are tenant configuration (ADMIN, `channel:write`).
 * Selling through checkout (B709), charging a saved card (B703) and the
 * gate (B704) are their own items.
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId, zNonNegMoney } from "./common.js";

const zName = z.string().min(1).max(80);

const zTier = z
  .object({
    tierId: zId,
    name: zName,
    price: zNonNegMoney,
    covers: z
      .object({
        namedAdults: z.union([z.literal(1), z.literal(2)]),
        householdChildren: z
          .object({ max: z.number().int().min(0).max(10), ageBand: z.object({ min: z.number().int().min(0), max: z.number().int().min(0) }).strict().optional() })
          .strict(),
        guestsPerDay: z.number().int().min(0).max(10),
      })
      .strict(),
    admission: z
      .object({ scope: z.enum(["ALL", "LISTED"]), productIds: z.array(zId).max(200).optional(), blackoutCalendarId: zId.optional() })
      .strict(),
    benefits: z
      .object({
        percentOffByCategory: z.record(z.string().min(1).max(40), z.number().int().min(0).max(10_000)),
        guestPasses: z.number().int().min(0).max(100),
        coupons: z.number().int().min(0).max(100),
        parking: z.boolean(),
        earlyHours: z.boolean(),
        events: z.boolean(),
      })
      .strict(),
    eligibility: z
      .object({
        residentZips: z.array(z.string().min(3).max(10)).max(500).optional(),
        seniorAge: z.number().int().min(0).max(120).optional(),
        childAge: z.number().int().min(0).max(25).optional(),
      })
      .strict(),
    fmv: zNonNegMoney.optional(),
    addOns: z.array(z.object({ addOnId: zId, name: zName, price: zNonNegMoney }).strict()).max(10),
  })
  .strict();

const zPlanKind = z.enum(["ANNUAL", "MONTHLY"]);
const zPlanStatus = z.enum(["DRAFT", "ACTIVE", "RETIRED"]);

const zPlan = z
  .object({
    planId: zId,
    name: zName,
    kind: zPlanKind,
    status: zPlanStatus,
    tiers: z.array(zTier),
    createdAt: zEpochMs,
    updatedAt: zEpochMs,
  })
  .strict();

const zNamedAdult = z.object({ name: zName }).strict();

const zCard = z
  .object({
    cardId: zId,
    holder: z.string().min(1),
    issuedAt: zEpochMs,
    validFrom: zEpochMs,
    validUntil: zEpochMs,
    revokedAt: zEpochMs.optional(),
    /** B704 (3) — when a gate admitted this card under the photoId switch; the ID ask stops from then on. */
    idCheckedAt: zEpochMs.optional(),
  })
  .strict();

const zMemberStatus = z.enum(["ACTIVE", "GRACE", "LAPSED", "CANCELLED", "GIFT_UNREDEEMED"]);

/** A member as every door answers it; `status` is the status AT READ TIME (grace and lapse derive from the term). */
const zMember = z
  .object({
    memberNumber: zId,
    planId: zId,
    tierId: zId,
    customerId: zId.optional(),
    email: z.string().optional(),
    zip: z.string().optional(),
    term: z.object({ start: zEpochMs, end: zEpochMs }).strict(),
    status: zMemberStatus,
    namedAdults: z.array(zNamedAdult),
    householdCount: z.number().int().min(0),
    addOns: z.array(zId),
    renewal: z.object({ method: z.enum(["MANUAL", "AUTO"]) }).strict(),
    allowances: z.object({ guestPasses: z.number().int().min(0), coupons: z.number().int().min(0), guestsByDay: z.record(z.string(), z.number().int().min(0)) }).strict(),
    cards: z.array(zCard),
    createdAt: zEpochMs,
    updatedAt: zEpochMs,
    cancelledAt: zEpochMs.optional(),
    cancelReason: z.string().optional(),
  })
  .strict();

const EXAMPLE_TIER = {
  tierId: "family",
  name: "Family",
  price: { amount: 18_900, currency: "USD" },
  covers: { namedAdults: 2, householdChildren: { max: 4, ageBand: { min: 0, max: 17 } }, guestsPerDay: 1 },
  admission: { scope: "ALL" },
  benefits: { percentOffByCategory: { food: 1_000 }, guestPasses: 2, coupons: 4, parking: true, earlyHours: false, events: true },
  eligibility: {},
  fmv: { amount: 4_000, currency: "USD" },
  addOns: [{ addOnId: "flex-guest", name: "Flex guest", price: { amount: 4_900, currency: "USD" } }],
} as const;

const EXAMPLE_PLAN = {
  planId: "annual",
  name: "Harbor Lights Membership",
  kind: "ANNUAL",
  status: "ACTIVE",
  tiers: [EXAMPLE_TIER],
  createdAt: 1_791_000_000_000,
  updatedAt: 1_791_000_000_000,
} as const;

const EXAMPLE_MEMBER = {
  memberNumber: "M12345678",
  planId: "annual",
  tierId: "family",
  customerId: "cust-1",
  email: "ada@example.org",
  term: { start: 1_791_000_000_000, end: 1_822_536_000_000 },
  status: "ACTIVE",
  namedAdults: [{ name: "Ada Lovelace" }, { name: "Charles Babbage" }],
  householdCount: 2,
  addOns: [],
  renewal: { method: "MANUAL" },
  allowances: { guestPasses: 2, coupons: 4, guestsByDay: {} },
  cards: [],
  createdAt: 1_791_000_000_000,
  updatedAt: 1_791_000_000_000,
} as const;

const BACK_OFFICE = ["STAFF:ADMIN", "STAFF:MANAGER"] as const;
const COUNTER = ["STAFF:ADMIN", "STAFF:MANAGER", "STAFF:SELLER"] as const;

/* ----------------------------------------------------------------- plans */

export const membershipPlanCreate = writeOp({
  name: "membership.plan.create",
  permission: "entitlement:write",
  principals: [...BACK_OFFICE],
  fields: { planId: zId, name: zName, kind: zPlanKind, status: zPlanStatus.optional(), tiers: z.array(zTier).min(1).max(12) },
  response: z.object({ plan: zPlan }).strict(),
  exampleRequest: { planId: "annual", name: "Harbor Lights Membership", kind: "ANNUAL", tiers: [EXAMPLE_TIER] },
  exampleResponse: { plan: EXAMPLE_PLAN },
});

export const membershipPlanUpdate = writeOp({
  name: "membership.plan.update",
  permission: "entitlement:write",
  principals: [...BACK_OFFICE],
  fields: { planId: zId, name: zName.optional(), status: zPlanStatus.optional(), tiers: z.array(zTier).min(1).max(12).optional() },
  response: z.object({ plan: zPlan }).strict(),
  exampleRequest: { planId: "annual", status: "RETIRED" },
  exampleResponse: { plan: { ...EXAMPLE_PLAN, status: "RETIRED" } },
});

export const membershipPlanGet = readOp({
  name: "membership.plan.get",
  permission: "catalog:read",
  principals: [...COUNTER, "CUSTOMER"],
  fields: { planId: zId },
  response: z.object({ plan: zPlan.optional() }).strict(),
  exampleRequest: { planId: "annual" },
  exampleResponse: { plan: EXAMPLE_PLAN },
});

/** A buyer sees only plans ON SALE; staff see every plan (drafts and retired included). */
export const membershipPlanList = readOp({
  name: "membership.plan.list",
  permission: "catalog:read",
  principals: [...COUNTER, "CUSTOMER"],
  fields: {},
  response: z.object({ plans: z.array(zPlan) }).strict(),
  exampleRequest: {},
  exampleResponse: { plans: [EXAMPLE_PLAN] },
});

/* --------------------------------------------------------------- members */

export const membershipCreate = writeOp({
  name: "membership.create",
  permission: "order:write",
  principals: [...COUNTER],
  fields: {
    planId: zId,
    tierId: zId,
    namedAdults: z.array(zNamedAdult).min(1).max(2),
    householdCount: z.number().int().min(0).max(10),
    addOns: z.array(zId).max(10).optional(),
    email: z.string().min(3).max(254).optional(),
    zip: z.string().min(3).max(10).optional(),
    gift: z.boolean().optional(),
  },
  response: z.object({ member: zMember }).strict(),
  exampleRequest: { planId: "annual", tierId: "family", namedAdults: [{ name: "Ada Lovelace" }, { name: "Charles Babbage" }], householdCount: 2, email: "ada@example.org" },
  exampleResponse: { member: EXAMPLE_MEMBER },
});

export const membershipRenew = writeOp({
  name: "membership.renew",
  permission: "order:write",
  principals: [...COUNTER],
  fields: { memberNumber: zId },
  response: z.object({ member: zMember }).strict(),
  exampleRequest: { memberNumber: "M12345678" },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, term: { start: 1_822_536_000_000, end: 1_854_072_000_000 } } },
});

/**
 * B703 (2c-iv) — auto-renew at the counter. ON hands back the gateway's
 * hosted card page (`setupUrl`) and records the words the member agreed to;
 * the member turns AUTO only when the gateway's signed "card saved" arrives.
 * OFF returns the member to manual renewal. No money moves here — the
 * renewal scheduler charges at the expiry (never Stripe Subscriptions).
 */
export const membershipAutorenewSet = writeOp({
  name: "membership.autorenew.set",
  permission: "order:write",
  principals: [...COUNTER],
  fields: {
    memberNumber: zId,
    on: z.boolean(),
    consentText: z.string().max(500).optional(),
    returnUrl: z.string().url().max(2048).optional(),
  },
  response: z.object({ member: zMember, setupUrl: z.string().optional() }).strict(),
  exampleRequest: { memberNumber: "M12345678", on: true, consentText: "Renew my membership each year with this card until I turn it off.", returnUrl: "https://admin.example.org/members/M12345678" },
  exampleResponse: { member: EXAMPLE_MEMBER, setupUrl: "https://checkout.stripe.com/c/pay/cs_test_123" },
});

export const membershipGet = readOp({
  name: "membership.get",
  owner: "customer",
  permission: "entitlement:read",
  principals: [...COUNTER, "CUSTOMER"],
  fields: { memberNumber: zId },
  response: z.object({ member: zMember.optional() }).strict(),
  exampleRequest: { memberNumber: "M12345678" },
  exampleResponse: { member: EXAMPLE_MEMBER },
});

/** Staff: the tenant's members (oldest first, `status` narrows); a CUSTOMER: its own. */
export const membershipList = readOp({
  name: "membership.list",
  owner: "customer",
  permission: "entitlement:read",
  principals: [...COUNTER, "CUSTOMER"],
  fields: { status: zMemberStatus.optional(), limit: z.number().int().min(1).max(500).optional() },
  response: z.object({ members: z.array(zMember) }).strict(),
  exampleRequest: {},
  exampleResponse: { members: [EXAMPLE_MEMBER] },
});

/** The counter's lookup: a member number (exact), a named adult's name (contains), an email (exact), or a card id (exact — the till sends a scanned card's id, B710). */
export const membershipSearch = readOp({
  name: "membership.search",
  permission: "entitlement:read",
  principals: [...COUNTER],
  fields: { query: z.string().min(1).max(254) },
  response: z.object({ members: z.array(zMember) }).strict(),
  exampleRequest: { query: "Lovelace" },
  exampleResponse: { members: [EXAMPLE_MEMBER] },
});

/** Memberships are non-refundable: a cancel ends the membership and retires its cards; no money moves. */
export const membershipCancel = writeOp({
  name: "membership.cancel",
  permission: "entitlement:write",
  principals: [...BACK_OFFICE],
  fields: { memberNumber: zId, reason: z.string().max(200).optional() },
  response: z.object({ member: zMember }).strict(),
  exampleRequest: { memberNumber: "M12345678", reason: "Moved away" },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, status: "CANCELLED", cancelledAt: 1_795_000_000_000, cancelReason: "Moved away" } },
});

/** Named adults are locked while the term runs unless an ADMIN changes them. */
export const membershipUpdate = writeOp({
  name: "membership.update",
  permission: "entitlement:write",
  principals: [...BACK_OFFICE],
  fields: {
    memberNumber: zId,
    namedAdults: z.array(zNamedAdult).min(1).max(2).optional(),
    householdCount: z.number().int().min(0).max(10).optional(),
    addOns: z.array(zId).max(10).optional(),
    email: z.string().min(3).max(254).optional(),
  },
  response: z.object({ member: zMember }).strict(),
  exampleRequest: { memberNumber: "M12345678", householdCount: 3 },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, householdCount: 3 } },
});

/**
 * One card per named adult, valid across the term, each with its signed
 * credential (`ttk1.…`, the ticket token shape). A re-issue retires every
 * live card first. The gate learns to admit them in B704.
 */
export const memberCardIssue = writeOp({
  name: "member.card.issue",
  permission: "order:write",
  principals: [...COUNTER],
  fields: { memberNumber: zId },
  response: z
    .object({
      memberNumber: zId,
      cards: z.array(z.object({ cardId: zId, holder: z.string().min(1), validFrom: zEpochMs, validUntil: zEpochMs, token: z.string().min(1) }).strict()),
    })
    .strict(),
  exampleRequest: { memberNumber: "M12345678" },
  exampleResponse: {
    memberNumber: "M12345678",
    cards: [{ cardId: "mc-1", holder: "Ada Lovelace", validFrom: 1_791_000_000_000, validUntil: 1_822_536_000_000, token: "ttk1.eyJleGFtcGxlIjp0cnVlfQ.key-1.c2ln" }],
  },
});

/* -------------------------------------------------------------- switches */

const zSwitchFields = {
  residency: z.boolean(),
  photoId: z.boolean(),
  facial: z.boolean(),
  blackouts: z.boolean(),
  reciprocal: z.boolean(),
  guestsPerDay: z.boolean(),
  fmvOnReceipt: z.boolean(),
  graceDays: z.number().int().min(0).max(90),
  giftVoucherDays: z.number().int().min(0).max(1_095),
};

const zSwitchesBody = z.object({ ...zSwitchFields, source: z.enum(["defaults", "config"]), updatedAt: zEpochMs.optional() }).strict();

const EXAMPLE_SWITCHES = {
  residency: false,
  photoId: false,
  facial: false,
  blackouts: false,
  reciprocal: false,
  guestsPerDay: false,
  fmvOnReceipt: false,
  graceDays: 14,
  giftVoucherDays: 0,
} as const;

export const membershipSettingsGet = readOp({
  name: "membership.settings.get",
  permission: "catalog:read",
  principals: [...COUNTER],
  fields: {},
  response: zSwitchesBody,
  exampleRequest: {},
  exampleResponse: { ...EXAMPLE_SWITCHES, source: "defaults" },
});

export const membershipSettingsSet = writeOp({
  name: "membership.settings.set",
  // Tenant configuration write — the channel-config scope (ADMIN-only), the ops.config.set wall.
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: zSwitchFields,
  response: zSwitchesBody,
  exampleRequest: { ...EXAMPLE_SWITCHES, residency: true, graceDays: 30 },
  exampleResponse: { ...EXAMPLE_SWITCHES, residency: true, graceDays: 30, source: "config", updatedAt: 1_791_000_000_000 },
});

/* ------------------------------------------------------------- blackouts */

/**
 * B704 (2) — the tenant's blackout calendars: named lists of VENUE days a
 * tier points at (`admission.blackoutCalendarId`). The gate reads them only
 * while the `blackouts` switch is on. Replaced whole on every set (≤ 20
 * calendars × ≤ 366 days); the domain judges each by name (422 with the
 * sentences), the schema only bounds the shape.
 */
const zBlackoutCalendar = z
  .object({
    calendarId: zId,
    name: z.string().max(80),
    days: z.array(z.object({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), reason: z.string().max(80) }).strict()).max(366),
  })
  .strict();

const zBlackoutsBody = z.object({ calendars: z.array(zBlackoutCalendar).max(20), updatedAt: zEpochMs.optional() }).strict();

const EXAMPLE_CALENDAR = { calendarId: "events", name: "Special events", days: [{ date: "2026-10-31", reason: "Halloween Spooktacular" }] };

export const membershipBlackoutsGet = readOp({
  name: "membership.blackouts.get",
  permission: "catalog:read",
  principals: [...COUNTER],
  fields: {},
  response: zBlackoutsBody,
  exampleRequest: {},
  exampleResponse: { calendars: [EXAMPLE_CALENDAR] },
});

export const membershipBlackoutsSet = writeOp({
  name: "membership.blackouts.set",
  // Tenant configuration write — the settings.set wall (ADMIN-only, channel:write).
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { calendars: z.array(zBlackoutCalendar).max(20) },
  response: zBlackoutsBody,
  exampleRequest: { calendars: [EXAMPLE_CALENDAR] },
  exampleResponse: { calendars: [EXAMPLE_CALENDAR], updatedAt: 1_791_000_000_000 },
});
